import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Link, Outlet, createFileRoute, redirect, useLocation } from "@tanstack/react-router";
import {
  Activity,
  Brain,
  Building2,
  CreditCard,
  DollarSign,
  FileText,
  Presentation,
  Gauge,
  Gift,
  HeartPulse,
  Layers,
  TrendingUp,
  UserCog,
  Users,
  Workflow,
} from "lucide-react";
import * as React from "react";
import { useMemo } from "react";
import { createPortal } from "react-dom";

import {
  Button,
  type NavGroup,
  type NavItem,
  PlatformAreaLayout,
  SurfaceEmptyState,
  cn,
} from "@ando/ui";

import { AdminRangePicker } from "@/features/admin/components/AdminRangePicker";
import type { AdminRangePreset } from "@/features/admin/hooks/useAdminRange";
import { platformAreaRouter } from "@/features/platform-area/router-adapter";
import { getSession, isPlatformAdmin } from "@ando/core/auth";
import { ModalBarSlotContext } from "../route";

/** Search params owned by the admin area — the global time-range filter. */
interface AdminAreaSearch {
  range?: AdminRangePreset;
  start?: string;
  end?: string;
}

const _RANGE_PRESETS = ["1d", "7d", "30d", "90d", "custom"];

/**
 * Sections whose data hooks accept the time range. Governance surfaces
 * (orgs / users / pricing) and Revenue (all-time) don't, so the picker is
 * hidden there to avoid showing a control that does nothing.
 */
const RANGE_AWARE_PATHS: readonly string[] = [
  "/admin",
  "/admin/cost-tracking",
  "/admin/engagement",
  "/admin/quality",
  "/admin/canvas",
  "/admin/automation",
  "/admin/health",
];

/**
 * Admin parent route — gates access via ``isPlatformAdmin(session)`` (role∈
 * {admin,superadmin} OR the legacy ``is_platform_admin`` flag, mirroring the BE
 * ``require_platform_admin``) and renders the unified ``PlatformAreaLayout`` rail.
 *
 * v1 reference: ``HypermemAdminPage.tsx`` (398 LoC) used a tab strip; v2
 * keeps the navigation but mounts each tab as its own route so the URL
 * is a stable deep-link and TanStack Query caches survive tab switches.
 *
 * Gate: ``beforeLoad`` redirects non-admins BEFORE the shell mounts (no
 * admin component renders, no admin API call fires). It reads the platform
 * flag from :func:`getSession` — the SAME session accessor the ``_authed``
 * auth guard uses, so it works identically on web (cookie) and the Tauri
 * desktop (localStorage fallback). ``is_platform_admin`` rides the session
 * payload (``saveUserInfo``), so it's present on both.
 *
 * NB: do NOT gate this on ``useHasCapability("admin")`` — "admin" is not a
 * manifest capability, and that hook FAILS OPEN on unknown slots (returns
 * ``satisfied`` so it never nags about connectors you lack), which would
 * grant the admin shell to every user. Defense in depth: every BE endpoint
 * also enforces ``require_platform_admin``.
 */
export const Route = createFileRoute("/_authed/_modal/admin")({
  beforeLoad: () => {
    // Platform-admin gate — ``role`` is the source of truth (``is_platform_admin``
    // is a legacy duplicate). ``isPlatformAdmin`` matches the BE
    // ``require_platform_admin`` (boolean OR role∈{admin,superadmin}), so a user
    // whose ROLE is admin is no longer wrongly locked out.
    if (!isPlatformAdmin(getSession())) {
      throw redirect({ to: "/workspace/conversations" });
    }
  },
  component: AdminShell,
  // Global admin time-range filter — inherited by every child section.
  validateSearch: (raw: Record<string, unknown>): AdminAreaSearch => {
    const range =
      typeof raw.range === "string" && _RANGE_PRESETS.includes(raw.range)
        ? (raw.range as AdminRangePreset)
        : undefined;
    return {
      range,
      start: typeof raw.start === "string" ? raw.start : undefined,
      end: typeof raw.end === "string" ? raw.end : undefined,
    };
  },
});

interface AdminNavItemDef {
  readonly to: string;
  readonly label: MessageDescriptor;
  readonly icon: NavItem["icon"];
}

interface AdminNavGroupDef {
  readonly eyebrow: MessageDescriptor;
  readonly items: readonly AdminNavItemDef[];
}

const NAV_GROUP_DEFS: readonly AdminNavGroupDef[] = [
  {
    eyebrow: msg`Metriche`,
    items: [
      { to: "/admin", label: msg`Overview`, icon: Activity },
      { to: "/admin/cost-tracking", label: msg`Cost tracking`, icon: DollarSign },
      { to: "/admin/revenue", label: msg`Revenue`, icon: TrendingUp },
      { to: "/admin/engagement", label: msg`Engagement`, icon: Users },
      { to: "/admin/quality", label: msg`Qualità`, icon: Gauge },
    ],
  },
  {
    eyebrow: msg`Sistema`,
    items: [
      { to: "/admin/hypermem", label: msg`HyperMem`, icon: Brain },
      { to: "/admin/canvas", label: msg`Canvas`, icon: Layers },
      { to: "/admin/automation", label: msg`Automation`, icon: Workflow },
      { to: "/admin/health", label: msg`Salute sistema`, icon: HeartPulse },
    ],
  },
  {
    eyebrow: msg`Governance`,
    items: [
      { to: "/admin/orgs", label: msg`Organizzazioni`, icon: Building2 },
      // Differentiate from "Engagement" (cohort metrics) — this is the
      // user-management surface (impersonate / role flip / disable).
      { to: "/admin/users", label: msg`Utenti`, icon: UserCog },
      // Billing hub — global markup + collection + models&plans (which live as
      // deep-linked sub-pages). Per-tenant billing is inside each ORG.
      { to: "/admin/billing", label: msg`Billing`, icon: CreditCard },
      // Demo invites — issue/manage client demo access (activation-based expiry).
      { to: "/admin/demo-inviti", label: msg`Demo Inviti`, icon: Gift },
      // CPQ — Ando Engine licence quotes (price, margin, e-mail-gated links).
      { to: "/admin/preventivi", label: msg`Preventivi`, icon: FileText },
      // Ando Lab decks (sales / investor), editable with Ando, e-mail-gated links.
      { to: "/admin/presentazioni", label: msg`Presentazioni`, icon: Presentation },
    ],
  },
];

function AdminShell(): React.ReactElement {
  const { i18n } = useLingui();
  // Defense in depth — ``beforeLoad`` already redirected non-admins; this
  // is the belt-and-suspenders render guard (same authoritative signal).
  const hasAccess = isPlatformAdmin(getSession());

  const navGroups: readonly NavGroup[] = useMemo(
    () =>
      NAV_GROUP_DEFS.map((group) => ({
        eyebrow: i18n._(group.eyebrow),
        items: group.items.map((item) => ({
          to: item.to,
          label: i18n._(item.label),
          icon: item.icon,
        })),
      })),
    [i18n],
  );

  if (!hasAccess) {
    return (
      <div className="flex h-full w-full items-center justify-center px-8">
        <SurfaceEmptyState
          icon={<Activity size={18} strokeWidth={1.75} />}
          title={i18n._(msg`Area amministrativa`)}
          description={i18n._(msg`Questa sezione è riservata agli admin di piattaforma.`)}
          action={
            <Button asChild size="sm">
              <Link to="/workspace/conversations">{i18n._(msg`Torna al workspace`)}</Link>
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-cream">
      {/* Il controllo "indietro" e' UNICO e vive nella shell condivisa
          (``_modal/route.tsx``, ``BackButton``): la #67 ha reso quella barra
          visibile su tutte le takeover, quindi il controllo in-flow che Admin
          dipingeva da se' sarebbe un doppione. Il ritorno all'origine
          memorizzata (``useTakeoverBack``) e' stato spostato nella shell. */}
      <div className="min-h-0 flex-1 overflow-hidden">
        <PlatformAreaLayout
          title={i18n._(msg`Admin`)}
          groups={navGroups}
          router={platformAreaRouter}
        >
          <AdminSectionFrame />
        </PlatformAreaLayout>
      </div>
    </div>
  );
}

/**
 * Wraps the section outlet with the global range picker. The picker only
 * shows on range-aware metric sections (see ``RANGE_AWARE_PATHS``).
 *
 * Il picker NON ha piu' una fascia propria: si aggancia al lato destro della
 * barra del back (``ModalBarSlotContext``), che e' alta 44px e li' era vuota.
 * Cosi' l'area guadagna ~41px di altezza su ogni sezione con il range, e il
 * controllo globale sta in alto con l'altra chrome globale invece di sembrare
 * parte del contenuto della sezione.
 *
 * La regola di visibilita' NON cambia: resta legata a ``RANGE_AWARE_PATHS``,
 * perche' mostrarlo dove non filtra nulla sarebbe un controllo che mente.
 */
function AdminSectionFrame(): React.ReactElement {
  const pathname = useLocation({ select: (l) => l.pathname });
  const showRange = RANGE_AWARE_PATHS.includes(pathname);
  const barSlot = React.useContext(ModalBarSlotContext);
  return (
    <div className="flex h-full min-h-0 flex-col">
      {showRange && barSlot ? createPortal(<AdminRangePicker />, barSlot) : null}
      <div className="min-h-0 flex-1 overflow-hidden">
        <Outlet />
      </div>
    </div>
  );
}
